import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../../../environments/environment';
import {SpAttachment, StudyProgrammeReq, StudyProgrammeRes} from '../models/osp';
import {Category, Evaluation, StudyProgrammeEvaluations} from '../models/saavs';
import {Meeting, StudyProgrammeWithMeetings} from '../models/spMeeting';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';

@Injectable()
export class SpService {

  constructor(private http: HttpClient, private translate: TranslateService) {
  }

  public getOsp(idStudyProgramme: number): Promise<StudyProgrammeRes> {
    return this.http.get<any>(`${environment.api_url}/study_programme/${idStudyProgramme}`).toPromise();
  }

  public postOsp(ospObject: StudyProgrammeRes | StudyProgrammeReq): Promise<StudyProgrammeRes> {
    //return this.http.post<any>(`${environment.api_url}/study_programme`, ospObject).toPromise();
    const body: any = {...ospObject};
    // Normalize faculty: if frontend passed a string, convert to object
    if (body.faculty && typeof body.faculty === 'string') {
      const maybeId = Number(body.faculty);
      body.faculty = Number.isInteger(maybeId) && maybeId > 0 ? {id: maybeId} : {name: body.faculty};
    }
    return this.http.post<any>(`${environment.api_url}/study_programme`, body).toPromise();
  }

  public postAttachment(attachment: any): Promise<SpAttachment> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(attachment)) {
      // @ts-ignore
      formData.append(key, value);
    }

    return this.http.post<any>(`${environment.api_url}/study_programme/${attachment.idStudyProgramme}/save-file`, formData).toPromise();
  }

  public getAttachments(idStudyProgramme: number): Promise<SpAttachment[]> {
    return this.http.get<any>(`${environment.api_url}/study_programme/${idStudyProgramme}/get-attachments`).toPromise();
  }

  public getAttachmentFile(idStudyProgramme: number, idAttachment: number): Promise<any> {
    return this.http.get<any>(`${environment.api_url}/study_programme/${idStudyProgramme}/get-attachment/${idAttachment}`).toPromise();
  }

  public deleteAttachment(idStudyProgramme: number, idAttachment: number): Promise<any> {
    return this.http.delete<any>(
      `${environment.api_url}/study_programme/${idStudyProgramme}/delete-attachment/${idAttachment}`)
      .toPromise();
  }

  public getEvaluationCriteria(type: string): Promise<Category[]> {
    return this.http.get<any>(`${environment.api_url}/saavs/${type}`).toPromise();
  }

  public getSPEvaluations(programmeId: string): Promise<StudyProgrammeEvaluations> {
    return this.http.get<any>(`${environment.api_url}/evaluation/sp/${programmeId}`).toPromise();
  }

  public mergeEvaluations(programmeId: string): Observable<Evaluation> {
    return this.http.get<Evaluation>(`${environment.api_url}/evaluation/merge/${programmeId}`);
  }

  public getSPEvaluation(evaluationId: string): Promise<StudyProgrammeEvaluations> {
    return this.http.get<any>(`${environment.api_url}/evaluation/${evaluationId}`).toPromise();
  }

  public copyEvaluation(evaluationId: number): Promise<Evaluation> {
    return this.http.get<any>(`${environment.api_url}/evaluation/copy/${evaluationId}`).toPromise();
  }

  public saveSPEvaluation(evaluation: Evaluation): Promise<Evaluation> {
    return this.http.post<any>(`${environment.api_url}/evaluation/`, evaluation).toPromise();
  }

  public deleteEvaluation(evaluationId: number): Promise<any> {
    return this.http.delete<any>(`${environment.api_url}/evaluation/${evaluationId}`).toPromise();
  }

  public postMeeting(meeting: Meeting): Promise<StudyProgrammeWithMeetings> {
    if (meeting.attachment !== null) {
      meeting.file = new File([meeting.attachment.data], meeting.attachment.name, {type: meeting.attachment.type});
      meeting.idFile = meeting.attachment.idFile;
    }
    if (meeting.file === null) {
      meeting.file = new File([], '');
    }
    const formData = new FormData();
    for (const [key, value] of Object.entries(meeting)) {
      formData.append(key, value);
    }

    return this.http.post<any>(`${environment.api_url}/meeting`, formData).toPromise();
  }

  public getMeeting(meetingId: string): Promise<StudyProgrammeWithMeetings> {
    return this.http.get<any>(`${environment.api_url}/meeting/${meetingId}`).toPromise();
  }

  public getMeetings(category: string, categoryId: string): Promise<StudyProgrammeWithMeetings> {
    return this.http.get<any>(`${environment.api_url}/meeting/${category}/${categoryId}`).toPromise();
  }

  public deleteMeetings(meetingId: number): Promise<any> {
    return this.http.delete<any>(`${environment.api_url}/meeting/${meetingId}`).toPromise();
  }

  public getMeetingAttachment(attachmentId: number): Promise<any> {
    return this.http.get(`${environment.api_url}/meeting/file/${attachmentId}`, {responseType: 'blob'}).toPromise();
  }

  public getSpName(programme: any): string {
    let title = programme.title;
    title = title.charAt(0).toUpperCase() + title.slice(1);
    let form = programme.formOfStudy === 'PRESENT' ? this.translate.instant('forms.osp.tabs.basic_data.section.basic_data.input.form_of_study.present')
      : this.translate.instant('forms.osp.tabs.basic_data.section.basic_data.input.form_of_study.external');
    form = form.charAt(0).toLowerCase() + form.slice(1);
    let spName = title + ' - ' + programme.faculty.name + ', ' + this.translate.instant('dashboard.study_programme_part.sp_subtitle.study_field') + programme.studyField + ', ';
    spName += programme.studyField2 ? programme.studyField2 + ', ' : '';
    spName += programme.degree + '. st, ' + form;
    return spName;
  }

  public countCriteriaEvaluations(evaluation: Evaluation): any {
    const criteriaCount = {
      A: 0,
      B: 0,
      C: 0,
      D: 0,
      X: 0
    };
    evaluation.scores.forEach(evl => {
      switch (evl.score) {
        case 'A':
          criteriaCount.A++;
          break;
        case 'B':
          criteriaCount.B++;
          break;
        case 'C':
          criteriaCount.C++;
          break;
        case 'D':
          criteriaCount.D++;
          break;
        default:
          criteriaCount.X++;
          break;
      }
    });
    return criteriaCount;
  }

}
